<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';

import { formatPhoneNumber } from '@/utils/strings/phone-number.util';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import ProHeaderTooltip from './ProHeaderTooltip.vue';
import { CONTACT_TITLE_TEST_ID, useContactDetailsCollapse } from './useContactDetailsCollapse';

const proProfileService = new ProProfileService();

const contactRow = ref<HTMLElement | null>(null);
const { truncateValues, iconsOnly, reset: resetContactRow } = useContactDetailsCollapse(contactRow);

const userProfile = computed(() => proProfileService.selectedProProfile);
const proLocation = computed(() => {
  if (!(userProfile.value?.city && userProfile.value?.state))
    return userProfile.value?.pro?.postalCode || '';
  return `${userProfile.value.city}, ${userProfile.value.state}`;
});
const phoneNumber = computed(() => formatPhoneNumber(userProfile.value?.phone));

// A different candidate brings different values, so the widths learned for the last one go.
watch(
  () => userProfile.value?.id,
  () => resetContactRow(),
);

// Values arrive after mount, and the row's own width does not change when they do. Different
// values need different widths, so the ones learned for what was there before have to go with
// them: kept, a width where the old values were comfortable invites longer ones back in below
// their floor. Same reason as a new candidate, so the same reset.
watch(
  [proLocation, phoneNumber, () => userProfile.value?.email, () => userProfile.value?.displayTitle],
  async () => {
    await nextTick();
    resetContactRow();
  },
);
</script>

<template>
  <div ref="contactRow" class="sensitive mt-1 flex space-x-2 text-xs">
    <div
      v-if="userProfile?.displayTitle"
      :data-test="CONTACT_TITLE_TEST_ID"
      class="min-w-0 truncate font-sans text-black"
    >
      {{ userProfile.displayTitle }}
    </div>
    <div v-if="proLocation" class="whitespace-nowrap text-shade-800">
      Located in {{ proLocation }}
    </div>
    <ProHeaderTooltip
      v-if="userProfile?.phone"
      :collapse-text="iconsOnly"
      :truncate-text="truncateValues"
      :text="phoneNumber"
      icon="phone"
    />
    <ProHeaderTooltip
      v-if="userProfile?.email"
      :collapse-text="iconsOnly"
      :truncate-text="truncateValues"
      :text="userProfile.email"
      icon="email"
    />
  </div>
</template>
